import {skipToken} from '@reduxjs/toolkit/query';
import {match} from 'ts-pattern';

import {isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageResponseBody} from '@dms/api/core';
import {useGetCustomerV2Query, useGetUserQuery} from '@dms/api/customer';

import {getCustomerName} from '../../../utils/getCustomerName';

interface ThreadEmailHeader {
  isIncoming: boolean;
  headerAddress: string;
  isSenderLoading: boolean;
}

// Resolves the email header line. Incoming mail shows the external contact; outgoing mail resolves
// the sending user and renders "User Name (user@email) > recipient@email".
export function useThreadEmailHeader(
  message: CustomerMessageResponseBody,
  showOutgoingRecipient = false
): ThreadEmailHeader {
  const isIncoming = message.direction === 'INCOMING';
  const senderId = isIncoming ? null : message.senderId;

  const {data: sender, isLoading: isSenderLoading} = useGetUserQuery(
    isNotNil(senderId) ? {id: senderId} : skipToken
  );

  const {data: customer, isLoading: isCustomerLoading} = useGetCustomerV2Query(
    {customerId: message.customerId ?? ''},
    {skip: !showOutgoingRecipient || isIncoming || isNilOrEmpty(message.customerId)}
  );

  const senderName = [sender?.firstName, sender?.lastName].filter(isNotNilOrEmpty).join(' ');
  const senderEmail = isIncoming ? message.contact : (sender?.email ?? '');
  const senderText = isNotNilOrEmpty(senderName) ? `${senderName} (${senderEmail})` : senderEmail;
  const recipientName = getCustomerName(customer);
  const recipientText = isNotNilOrEmpty(recipientName)
    ? `${recipientName} (${message.contact})`
    : message.contact;
  const headerAddress = match({
    isIncoming,
    showOutgoingRecipient,
    hasSenderText: isNotNilOrEmpty(senderText),
  })
    .with({isIncoming: true}, () => senderText)
    .with({showOutgoingRecipient: true}, () => recipientText)
    .with({hasSenderText: true}, () => `${senderText} > ${message.contact}`)
    .otherwise(() => message.contact);

  return {
    isIncoming,
    headerAddress,
    isSenderLoading: isSenderLoading || isCustomerLoading,
  };
}
